import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Check,
  Mic,
  Pause,
  Play,
  Pencil,
  Search,
  Sparkles,
  ArrowLeft,
  X,
} from 'lucide-react';
import { toast } from 'react-toastify';
import AiLoader from '../../EmbryoGradingAI/AiLoader';
import styles from './DocumentIntelligencePatientDetail.module.css';

import type {
  ExtractionApiResponse,
  ExtractionTypeOption,
} from '../VoiceDataEntry/AIVoiceExtraction.types';

import {
  buildAiSummaryLines,
  convertTranscriptToExtraction,
  EXTRACTION_TYPE_OPTIONS,
  formatLabel,
  getStructuredExtractionData,
  hasMeaningfulValue,
} from '../VoiceDataEntry/AIVoiceExtraction.api';

import {
  buildSummaryAiNotes,
  checkPatientHasActiveCycle,
  normalizeEmrDataForDocumentType,
  resolveDocumentType,
  saveSummaryNotes,
  saveVoiceExtraction,
  type VoiceDocumentType,
} from '../VoiceDataEntry/AIVoiceExtraction.saveApi';

import {
  createSpeechWebSocket,
  getMicErrorMessage,
  getSpeechWsUrl,
  requestMicrophonePermission,
  startAudioStreaming,
  stopAudioCapture,
} from '../VoiceDataEntry/AIVoiceChatBot.api';

type Props = {
  patientId: string | number;
  transcriptSeed?: string;
  title?: string;
  options?: ExtractionTypeOption[];
  onSaved?: () => void;
};

type FlowStep =
  | 'welcome'
  | 'recording'
  | 'transcript'
  | 'select-page'
  | 'loading'
  | 'summary'
  | 'preview'
  | 'error';

type EditablePreviewMap = Record<string, string>;

type PreviewItem =
  | {
      kind: 'section';
      id: string;
      label: string;
      depth: number;
    }
  | {
      kind: 'field';
      id: string;
      label: string;
      value: string;
      depth: number;
      isMultiline: boolean;
    };

type PendingNoCycleFallback = {
  documentType: VoiceDocumentType;
  editedStructuredData: Record<string, unknown>;
};

type MicPermissionState = 'prompt' | 'granted' | 'denied' | 'unsupported';

type LocalAudioCaptureRefs = {
  processorRef: React.MutableRefObject<ScriptProcessorNode | null>;
  sourceNodeRef: React.MutableRefObject<MediaStreamAudioSourceNode | null>;
  audioContextRef: React.MutableRefObject<AudioContext | null>;
  mediaStreamRef: React.MutableRefObject<MediaStream | null>;
};

const mergeTranscript = (base: string, live: string): string => {
  const left = base.trim();
  const right = live.trim();

  if (!left) return right;
  if (!right) return left;

  return `${left} ${right}`.replace(/\s+/g, ' ').trim();
};

const valueToText = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return '';

  if (Array.isArray(value)) {
    return value
      .filter((item) => hasMeaningfulValue(item))
      .map((item) => valueToText(item))
      .filter(Boolean)
      .join(', ');
  }

  if (typeof value === 'object') {
    return Object.entries(value as Record<string, unknown>)
      .filter(([, nested]) => hasMeaningfulValue(nested))
      .map(([key, nested]) => `${formatLabel(key)}: ${valueToText(nested)}`)
      .join(', ');
  }

  return String(value);
};

const isPrimitiveValue = (value: unknown) =>
  value === null || value === undefined || typeof value !== 'object';

const getArrayItemTitle = (arrayKey: string, item: unknown, index: number) => {
  if (typeof item === 'object' && item !== null && !Array.isArray(item)) {
    const record = item as Record<string, unknown>;
    const preferredKeys = [
      'day',
      'embryo_number',
      'oocyte_number',
      'title',
      'name',
      'label',
      'cycle_number',
      'date',
    ];

    for (const key of preferredKeys) {
      const candidate = record[key];
      if (typeof candidate === 'string' && candidate.trim()) {
        return candidate.trim();
      }
    }
  }

  return `${formatLabel(arrayKey)} ${index + 1}`;
};

const buildPreviewItems = (
  input: Record<string, unknown>,
  path = '',
  depth = 0,
): PreviewItem[] => {
  const items: PreviewItem[] = [];

  Object.entries(input).forEach(([key, value]) => {
    if (!hasMeaningfulValue(value)) return;

    const currentPath = path ? `${path}.${key}` : key;
    const label = formatLabel(key);

    if (Array.isArray(value)) {
      const meaningfulItems = value.filter((item) => hasMeaningfulValue(item));
      if (!meaningfulItems.length) return;

      if (meaningfulItems.every((item) => isPrimitiveValue(item))) {
        const text = meaningfulItems.map((item) => String(item)).join(', ');
        items.push({
          kind: 'field',
          id: currentPath,
          label,
          value: text,
          depth,
          isMultiline:
            text.length > 52 || text.includes(',') || text.includes('\n'),
        });
        return;
      }

      items.push({
        kind: 'section',
        id: `${currentPath}.__section`,
        label,
        depth,
      });

      meaningfulItems.forEach((item, index) => {
        const itemPath = `${currentPath}[${index}]`;

        if (typeof item === 'object' && item !== null && !Array.isArray(item)) {
          items.push({
            kind: 'section',
            id: `${itemPath}.__section`,
            label: getArrayItemTitle(key, item, index),
            depth: depth + 1,
          });

          items.push(
            ...buildPreviewItems(
              item as Record<string, unknown>,
              itemPath,
              depth + 2,
            ),
          );
          return;
        }

        const text = valueToText(item);
        if (!text) return;

        items.push({
          kind: 'field',
          id: itemPath,
          label: getArrayItemTitle(key, item, index),
          value: text,
          depth: depth + 1,
          isMultiline:
            text.length > 52 || text.includes(',') || text.includes('\n'),
        });
      });

      return;
    }

    if (typeof value === 'object' && value !== null) {
      items.push({
        kind: 'section',
        id: `${currentPath}.__section`,
        label,
        depth,
      });

      items.push(
        ...buildPreviewItems(
          value as Record<string, unknown>,
          currentPath,
          depth + 1,
        ),
      );
      return;
    }

    const text = String(value).trim();

    items.push({
      kind: 'field',
      id: currentPath,
      label,
      value: text,
      depth,
      isMultiline:
        text.length > 52 || text.includes('\n') || text.includes(','),
    });
  });

  return items;
};

const buildEditablePreviewValueMap = (
  items: PreviewItem[],
): EditablePreviewMap => {
  return items.reduce<EditablePreviewMap>((acc, item) => {
    if (item.kind === 'field') {
      acc[item.id] = item.value;
    }
    return acc;
  }, {});
};

const parsePathTokens = (path: string): Array<string | number> => {
  const tokens: Array<string | number> = [];

  path.split('.').forEach((segment) => {
    const regex = /([^[\]]+)|\[(\d+)\]/g;
    let match: RegExpExecArray | null;

    while ((match = regex.exec(segment)) !== null) {
      if (match[1]) tokens.push(match[1]);
      if (match[2]) tokens.push(Number(match[2]));
    }
  });

  return tokens;
};

const setValueAtPath = (
  target: Record<string, unknown> | unknown[],
  pathTokens: Array<string | number>,
  value: string,
) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let current: any = target;

  pathTokens.forEach((token, index) => {
    const isLast = index === pathTokens.length - 1;

    if (isLast) {
      current[token] = value;
      return;
    }

    const nextToken = pathTokens[index + 1];
    const shouldCreateArray = typeof nextToken === 'number';

    if (
      current[token] === undefined ||
      current[token] === null ||
      (shouldCreateArray && !Array.isArray(current[token])) ||
      (!shouldCreateArray &&
        (typeof current[token] !== 'object' || Array.isArray(current[token])))
    ) {
      current[token] = shouldCreateArray ? [] : {};
    }

    current = current[token];
  });
};

const applyEditedValuesToStructuredData = (
  structuredData: Record<string, unknown>,
  editedValues: EditablePreviewMap,
): Record<string, unknown> => {
  const cloned = JSON.parse(JSON.stringify(structuredData)) as Record<
    string,
    unknown
  >;

  Object.entries(editedValues).forEach(([fieldPath, value]) => {
    setValueAtPath(cloned, parsePathTokens(fieldPath), value);
  });

  return cloned;
};

const asNumber = (...values: unknown[]) => {
  for (const value of values) {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string' && value.trim()) {
      const parsed = Number(value.trim());
      if (!Number.isNaN(parsed)) return parsed;
    }
  }
  return null;
};

const getDoctorNameFromResponse = (
  documentType: VoiceDocumentType,
  response: ExtractionApiResponse | null,
): string => {
  if (!response) return '';

  const responseRecord = response as unknown as Record<string, unknown>;
  const emrData = responseRecord?.emr_data as
    | Record<string, unknown>
    | undefined;

  if (documentType === 'embryo_grading') {
    const embryoCulture = emrData?.embryo_culture as
      | Record<string, unknown>
      | undefined;
    const cultureDays = embryoCulture?.culture_days as
      | Array<Record<string, unknown>>
      | undefined;

    if (!Array.isArray(cultureDays)) return '';

    const found = cultureDays.find(
      (item) =>
        typeof item?.embryologist === 'string' &&
        item.embryologist.trim().length > 0,
    );

    return (found?.embryologist as string) || '';
  }

  if (documentType === 'oocyte_grading') {
    const oocyte = emrData?.oocyte_grading as
      | Record<string, unknown>
      | undefined;
    return (oocyte?.embryologist as string) || '';
  }

  const generalInfo = emrData?.general_info as
    | Record<string, unknown>
    | undefined;
  return (generalInfo?.referring_doctor as string) || '';
};

const formatDuration = (totalSeconds: number) => {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const pad = (value: number) => String(value).padStart(2, '0');
  return `${pad(hours)} : ${pad(minutes)} : ${pad(seconds)}`;
};

const EmbeddedVoiceToTextWorkbench: React.FC<Props> = ({
  patientId,
  transcriptSeed = '',
  title = 'AI Voice Data Entry',
  options = EXTRACTION_TYPE_OPTIONS,
  onSaved,
}) => {
  const [step, setStep] = useState<FlowStep>('welcome');
  const [searchValue, setSearchValue] = useState('');
  const [selectedType, setSelectedType] = useState<ExtractionTypeOption | null>(
    null,
  );

  const [transcriptText, setTranscriptText] = useState(transcriptSeed || '');
  const [isTranscriptEditable, setIsTranscriptEditable] = useState(false);

  const [micPermission, setMicPermission] =
    useState<MicPermissionState>('prompt');
  const [elapsedSeconds, setElapsedSeconds] = useState(0);

  const [extractionError, setExtractionError] = useState('');
  const [aiSummaryLines, setAiSummaryLines] = useState<string[]>([]);
  const [extractionResponse, setExtractionResponse] =
    useState<ExtractionApiResponse | null>(null);
  const [editablePreviewValues, setEditablePreviewValues] =
    useState<EditablePreviewMap>({});

  const [isSavingSummary, setIsSavingSummary] = useState(false);
  const [isSavingEmr, setIsSavingEmr] = useState(false);
  const [isCheckingCycle, setIsCheckingCycle] = useState(false);
  const [showNoActiveCycleModal, setShowNoActiveCycleModal] = useState(false);
  const [pendingNoCycleFallback, setPendingNoCycleFallback] =
    useState<PendingNoCycleFallback | null>(null);

  const finalTranscriptRef = useRef<string>(transcriptSeed || '');
  const timerRef = useRef<number | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const closeReasonRef = useRef<'manual' | 'cancel' | null>(null);
  const isRecordingRef = useRef(false);

  const processorRef = useRef<ScriptProcessorNode | null>(null);
  const sourceNodeRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);

  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const wsConnectPromiseRef = useRef<Promise<WebSocket> | null>(null);

  const transcriptRef = useRef(transcriptSeed || '');
  const sessionBaseTranscriptRef = useRef('');
  const isFinalizingRef = useRef(false);
  const pauseRequestedRef = useRef(false);
  const pendingReviewRef = useRef(false);
  const stopFallbackTimerRef = useRef<number | null>(null);

  const audioCaptureRefs = useMemo<LocalAudioCaptureRefs>(
    () => ({
      mediaStreamRef,
      audioContextRef,
      processorRef,
      sourceNodeRef,
    }),
    [],
  );

  const audioRefs = useMemo<LocalAudioCaptureRefs>(
    () => ({
      processorRef,
      sourceNodeRef,
      audioContextRef,
      mediaStreamRef,
    }),
    [],
  );

  const clearStopFallbackTimer = useCallback(() => {
    if (stopFallbackTimerRef.current !== null) {
      window.clearTimeout(stopFallbackTimerRef.current);
      stopFallbackTimerRef.current = null;
    }
  }, []);

  const closeSocket = useCallback(() => {
    if (wsRef.current) {
      try {
        wsRef.current.close();
      } catch {
        // ignore
      }
    }

    wsRef.current = null;
    wsConnectPromiseRef.current = null;
  }, []);

  const handleStopAudioCapture = useCallback(async () => {
    await stopAudioCapture(audioCaptureRefs);
  }, [audioCaptureRefs]);

  const buildActionPayload = useCallback(
    (action: 'START' | 'STOP') => {
      if (patientId) {
        return { action, patientId };
      }
      return { action };
    },
    [patientId],
  );

  const completePauseLocally = useCallback(
    async (incomingTranscript?: string) => {
      clearStopFallbackTimer();
      await handleStopAudioCapture();

      isRecordingRef.current = false;
      setIsRecording(false);
      setIsPaused(true);
      setIsStarting(false);

      const merged = (
        incomingTranscript ??
        transcriptRef.current ??
        sessionBaseTranscriptRef.current ??
        ''
      ).trim();

      transcriptRef.current = merged;
      finalTranscriptRef.current = merged;
      setTranscriptText(merged);
    },
    [clearStopFallbackTimer, handleStopAudioCapture],
  );

  const finalizeRecording = useCallback(
    async (incomingTranscript?: string) => {
      if (isFinalizingRef.current) return;
      isFinalizingRef.current = true;

      clearStopFallbackTimer();
      await handleStopAudioCapture();

      isRecordingRef.current = false;
      setIsRecording(false);
      setIsPaused(false);
      setIsStarting(false);

      const merged = (
        incomingTranscript ??
        transcriptRef.current ??
        sessionBaseTranscriptRef.current ??
        ''
      ).trim();

      transcriptRef.current = merged;
      finalTranscriptRef.current = merged;
      setTranscriptText(merged);

      if (pendingReviewRef.current) {
        pendingReviewRef.current = false;
        setStep('transcript');
      }

      isFinalizingRef.current = false;
    },
    [clearStopFallbackTimer, handleStopAudioCapture],
  );

  const connectWebSocket = useCallback(
    async (showErrorToUser = false): Promise<WebSocket> => {
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        return wsRef.current;
      }

      if (wsConnectPromiseRef.current) {
        return wsConnectPromiseRef.current;
      }

      wsConnectPromiseRef.current = createSpeechWebSocket({
        url: getSpeechWsUrl(),
        timeoutMs: 8000,
        onOpen: (ws) => {
          wsRef.current = ws;
          wsConnectPromiseRef.current = null;
        },
        onTranscript: (incomingTranscript) => {
          const merged = mergeTranscript(
            sessionBaseTranscriptRef.current,
            incomingTranscript || '',
          );

          transcriptRef.current = merged;
          finalTranscriptRef.current = merged;
          setTranscriptText(merged);
        },
        onCompleted: async (incomingTranscript) => {
          const merged = mergeTranscript(
            sessionBaseTranscriptRef.current,
            incomingTranscript || '',
          );

          if (pauseRequestedRef.current) {
            pauseRequestedRef.current = false;
            await completePauseLocally(merged);
            return;
          }

          await finalizeRecording(merged);
        },
        onUnexpectedClose: async () => {
          wsRef.current = null;
          wsConnectPromiseRef.current = null;

          if (pauseRequestedRef.current) {
            pauseRequestedRef.current = false;
            await completePauseLocally(transcriptRef.current);
            return;
          }

          if (isRecordingRef.current) {
            await finalizeRecording(transcriptRef.current);
          }
        },
        onError: () => {
          wsRef.current = null;
          wsConnectPromiseRef.current = null;
        },
      }).catch((error) => {
        wsRef.current = null;
        wsConnectPromiseRef.current = null;

        if (showErrorToUser) {
          setExtractionError(getMicErrorMessage(error));
        }

        throw error;
      });

      return wsConnectPromiseRef.current;
    },
    [completePauseLocally, finalizeRecording],
  );

  const ensureMicrophonePermission = useCallback(async (): Promise<boolean> => {
    const result = await requestMicrophonePermission(micPermission);
    setMicPermission(result.permission as MicPermissionState);

    if (!result.allowed && result.message) {
      setExtractionError(result.message);
    }

    return result.allowed;
  }, [micPermission]);

  const handleStartSession = useCallback(
    async (appendExistingTranscript = false) => {
      if (isStarting) return;

      setStep('recording');
      setIsStarting(true);
      setExtractionError('');
      pauseRequestedRef.current = false;
      clearStopFallbackTimer();

      try {
        if (!appendExistingTranscript) {
          sessionBaseTranscriptRef.current = '';
          transcriptRef.current = '';
          finalTranscriptRef.current = '';
          setTranscriptText('');
          setElapsedSeconds(0);
        } else {
          const existing =
            transcriptRef.current.trim() || transcriptText.trim();
          sessionBaseTranscriptRef.current = existing;
          transcriptRef.current = existing;
          finalTranscriptRef.current = existing;
        }

        const hasPermission = await ensureMicrophonePermission();

        if (!hasPermission) {
          setIsStarting(false);
          setIsRecording(false);
          setIsPaused(false);
          setStep(finalTranscriptRef.current.trim() ? 'transcript' : 'welcome');
          return;
        }

        const ws = await connectWebSocket(true);

        if (!ws || ws.readyState !== WebSocket.OPEN) {
          throw new Error('WebSocket not connected.');
        }

        await handleStopAudioCapture();

        isRecordingRef.current = true;
        await startAudioStreaming(ws, audioCaptureRefs, isRecordingRef);

        ws.send(JSON.stringify(buildActionPayload('START')));

        setIsRecording(true);
        setIsPaused(false);
        setIsStarting(false);
        setStep('recording');
      } catch (error) {
        isRecordingRef.current = false;
        setIsRecording(false);
        setIsPaused(false);
        setIsStarting(false);
        setExtractionError(getMicErrorMessage(error));
        await handleStopAudioCapture();
        setStep(finalTranscriptRef.current.trim() ? 'transcript' : 'welcome');
      }
    },
    [
      isStarting,
      transcriptText,
      clearStopFallbackTimer,
      ensureMicrophonePermission,
      connectWebSocket,
      handleStopAudioCapture,
      audioCaptureRefs,
      buildActionPayload,
    ],
  );

  const requestPauseFromServer = useCallback(async () => {
    if (!isRecording) return;

    clearStopFallbackTimer();
    pauseRequestedRef.current = true;

    const ws = wsRef.current;

    if (!ws || ws.readyState !== WebSocket.OPEN) {
      pauseRequestedRef.current = false;
      await completePauseLocally(transcriptRef.current);
      return;
    }

    try {
      ws.send(JSON.stringify(buildActionPayload('STOP')));

      stopFallbackTimerRef.current = window.setTimeout(() => {
        pauseRequestedRef.current = false;
        void completePauseLocally(transcriptRef.current);
      }, 1500);
    } catch {
      pauseRequestedRef.current = false;
      await completePauseLocally(transcriptRef.current);
    }
  }, [
    isRecording,
    clearStopFallbackTimer,
    completePauseLocally,
    buildActionPayload,
  ]);

  const requestStopFromServer = useCallback(async () => {
    clearStopFallbackTimer();

    const ws = wsRef.current;

    if (!ws || ws.readyState !== WebSocket.OPEN) {
      await finalizeRecording(transcriptRef.current);
      return;
    }

    try {
      ws.send(JSON.stringify(buildActionPayload('STOP')));

      stopFallbackTimerRef.current = window.setTimeout(() => {
        void finalizeRecording(transcriptRef.current);
      }, 1500);
    } catch {
      await finalizeRecording(transcriptRef.current);
    }
  }, [buildActionPayload, clearStopFallbackTimer, finalizeRecording]);

  const handlePauseResume = useCallback(async () => {
    if (isStarting) return;

    if (isRecording) {
      await requestPauseFromServer();
      return;
    }

    await handleStartSession(Boolean(transcriptRef.current.trim()));
  }, [isStarting, isRecording, requestPauseFromServer, handleStartSession]);

  const handleCancelRecording = useCallback(async () => {
    pendingReviewRef.current = false;
    pauseRequestedRef.current = false;
    clearStopFallbackTimer();

    await handleStopAudioCapture();

    isRecordingRef.current = false;
    setIsRecording(false);
    setIsPaused(false);
    setIsStarting(false);

    transcriptRef.current = '';
    finalTranscriptRef.current = '';
    sessionBaseTranscriptRef.current = '';
    setTranscriptText('');
    setElapsedSeconds(0);
    closeSocket();
    setStep('welcome');
  }, [clearStopFallbackTimer, handleStopAudioCapture, closeSocket]);

  const handleNextToReview = useCallback(async () => {
    const text = transcriptRef.current.trim() || transcriptText.trim();

    if (!text) {
      setExtractionError('No speech detected yet.');
      return;
    }

    pendingReviewRef.current = true;
    await requestStopFromServer();
  }, [transcriptText, requestStopFromServer]);

  useEffect(() => {
    if (!isRecording || isPaused) return;

    const timer = window.setInterval(() => {
      setElapsedSeconds((prev) => prev + 1);
    }, 1000);

    return () => window.clearInterval(timer);
  }, [isRecording, isPaused]);

  useEffect(() => {
    return () => {
      clearStopFallbackTimer();
      void stopAudioCapture(audioCaptureRefs);
      closeSocket();
    };
  }, [audioCaptureRefs, clearStopFallbackTimer, closeSocket]);

  const stopTimer = useCallback(() => {
    if (timerRef.current) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const cleanupStreaming = useCallback(async () => {
    isRecordingRef.current = false;
    stopTimer();

    try {
      wsRef.current?.close();
    } catch {
      // ignore
    }

    wsRef.current = null;
    await stopAudioCapture(audioRefs);
  }, [audioRefs, stopTimer]);

  const resetWorkbench = useCallback(
    async (keepSeed = false) => {
      closeReasonRef.current = 'cancel';
      await cleanupStreaming();

      const nextSeed = keepSeed ? transcriptSeed || '' : '';

      finalTranscriptRef.current = nextSeed;
      setTranscriptText(nextSeed);
      setSearchValue('');
      setSelectedType(null);
      setExtractionError('');
      setAiSummaryLines([]);
      setExtractionResponse(null);
      setEditablePreviewValues({});
      setPendingNoCycleFallback(null);
      setShowNoActiveCycleModal(false);
      setIsTranscriptEditable(false);
      setElapsedSeconds(0);
      setStep(nextSeed.trim() ? 'transcript' : 'welcome');

      window.setTimeout(() => {
        closeReasonRef.current = null;
      }, 0);
    },
    [cleanupStreaming, transcriptSeed],
  );

  useEffect(() => {
    setTranscriptText(transcriptSeed || '');
    finalTranscriptRef.current = transcriptSeed || '';
    setSearchValue('');
    setSelectedType(null);
    setExtractionError('');
    setAiSummaryLines([]);
    setExtractionResponse(null);
    setEditablePreviewValues({});
    setPendingNoCycleFallback(null);
    setShowNoActiveCycleModal(false);
    setIsTranscriptEditable(false);
    setElapsedSeconds(0);
    setStep((transcriptSeed || '').trim() ? 'transcript' : 'welcome');
  }, [transcriptSeed]);

  useEffect(() => {
    return () => {
      void cleanupStreaming();
    };
  }, [cleanupStreaming]);

  const filteredOptions = useMemo(() => {
    const q = searchValue.trim().toLowerCase();
    if (!q) return options;

    return options.filter(
      (option) =>
        option.label.toLowerCase().includes(q) ||
        option.description.toLowerCase().includes(q),
    );
  }, [options, searchValue]);

  const structuredExtractionData = useMemo<Record<
    string,
    unknown
  > | null>(() => {
    if (!extractionResponse) return null;
    return getStructuredExtractionData(extractionResponse) as Record<
      string,
      unknown
    >;
  }, [extractionResponse]);

  const previewItems = useMemo(() => {
    if (!structuredExtractionData) return [];
    const base = buildPreviewItems(structuredExtractionData);

    return base.map((item) =>
      item.kind === 'field'
        ? { ...item, value: editablePreviewValues[item.id] ?? item.value }
        : item,
    );
  }, [structuredExtractionData, editablePreviewValues]);

  const summaryHeading = useMemo(() => {
    if (!selectedType) return 'AI Summary';
    return `${selectedType.label} AI Summary`;
  }, [selectedType]);

  const selectedDocumentType = useMemo(
    () => resolveDocumentType(selectedType),
    [selectedType],
  );

  const resolvedTranscriptionId = useMemo(() => {
    const record = extractionResponse as unknown as Record<
      string,
      unknown
    > | null;
    return asNumber(record?.transcription_id, record?.transcriptionId);
  }, [extractionResponse]);

  const resolvedEmrId = useMemo(() => {
    const record = extractionResponse as unknown as Record<
      string,
      unknown
    > | null;
    return asNumber(record?.emr_id, record?.emrId);
  }, [extractionResponse]);

  const isCycleRequiredDocumentType =
    selectedDocumentType === 'embryo_grading' ||
    selectedDocumentType === 'oocyte_grading';

  const handleProceedFromTranscript = () => {
    if (!transcriptText.trim()) {
      setExtractionError(
        'Transcript is empty. Please record or paste transcript.',
      );
      return;
    }

    setExtractionError('');
    setStep('select-page');
  };

  const runExtraction = async (option: ExtractionTypeOption) => {
    if (!transcriptText.trim()) {
      setExtractionError(
        'Transcript is empty. Please add transcript text before extracting.',
      );
      setStep('error');
      return;
    }

    setSelectedType(option);
    setStep('loading');
    setExtractionError('');
    setExtractionResponse(null);
    setAiSummaryLines([]);
    setEditablePreviewValues({});
    setPendingNoCycleFallback(null);
    setShowNoActiveCycleModal(false);

    try {
      const response = await convertTranscriptToExtraction(
        transcriptText,
        option,
      );
      const summaryLines = buildAiSummaryLines(response, option);
      const structuredData = getStructuredExtractionData(response) as Record<
        string,
        unknown
      >;
      const previewStructure = buildPreviewItems(structuredData);

      setExtractionResponse(response);
      setAiSummaryLines(summaryLines);
      setEditablePreviewValues(buildEditablePreviewValueMap(previewStructure));
      setStep('summary');
    } catch (error) {
      setExtractionError(
        error instanceof Error
          ? error.message
          : 'Failed to extract transcript.',
      );
      setStep('error');
    }
  };

  const handleSaveSummaryToNotes = async () => {
    if (!selectedType || !selectedDocumentType || !extractionResponse) {
      toast.error('No AI extraction result is available to save.');
      return;
    }

    if (!patientId) {
      toast.error('Patient ID is missing.');
      return;
    }

    try {
      setIsSavingSummary(true);

      const aiNotes = buildSummaryAiNotes({
        option: selectedType,
        documentType: selectedDocumentType,
        summaryHeading,
        summaryLines: aiSummaryLines,
        extractionResponse,
        transcriptionId: resolvedTranscriptionId,
        emrId: resolvedEmrId,
        saveContext: 'manual_summary_save',
        cycleValidation: {
          checked: false,
          hasActiveCycle: null,
        },
      });

      await saveSummaryNotes({
        patientId: Number(patientId),
        aiNotes,
        duration: '0',
        type: 'voice_to_text',
        document_name: '',
        document_type: selectedDocumentType ?? '',
        doctor_name: getDoctorNameFromResponse(
          selectedDocumentType,
          extractionResponse,
        ),
        source: selectedType?.label ?? '',
        call_trascript: transcriptText,
      });

      toast.success('Summary saved successfully.');
      onSaved?.();
    } catch (error) {
      toast.error(
        error instanceof Error
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          ? (error as any)?.errors?.[0]?.detail || error.message
          : 'Failed to save summary.'
      );
    } finally {
      setIsSavingSummary(false);
    }
  };

  const handleSaveFallbackSummaryFromModal = async () => {
    if (
      !selectedType ||
      !selectedDocumentType ||
      !extractionResponse ||
      !patientId ||
      !pendingNoCycleFallback
    ) {
      toast.error('Unable to save fallback summary.');
      return;
    }

    try {
      setIsSavingSummary(true);

      const aiNotes = buildSummaryAiNotes({
        option: selectedType,
        documentType: selectedDocumentType,
        summaryHeading,
        summaryLines: aiSummaryLines,
        extractionResponse,
        transcriptionId: resolvedTranscriptionId,
        emrId: resolvedEmrId,
        editedStructuredData: pendingNoCycleFallback.editedStructuredData,
        saveContext: 'no_active_cycle_fallback',
        cycleValidation: {
          checked: true,
          hasActiveCycle: false,
        },
      });

      await saveSummaryNotes({
        patientId: Number(patientId),
        aiNotes,
        duration: '0',
        type: 'voice_to_text',
        document_name: '',
        document_type: selectedDocumentType ?? '',
        doctor_name: getDoctorNameFromResponse(
          selectedDocumentType,
          extractionResponse,
        ),
        source: selectedType?.label ?? '',
        call_trascript: transcriptText,
      });

      setShowNoActiveCycleModal(false);
      setPendingNoCycleFallback(null);
      toast.success('Saved to summary successfully.');
      onSaved?.();
    } catch (error) {
      toast.error(
        error instanceof Error
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          ? (error as any)?.errors?.[0]?.detail || error.message
          : 'Failed to save fallback summary.',
      );
    } finally {
      setIsSavingSummary(false);
    }
  };

  const handleSaveToEMR = async () => {
    if (!selectedType || !selectedDocumentType || !structuredExtractionData) {
      toast.error('No structured extraction data is available to save.');
      return;
    }

    if (!patientId) {
      toast.error('Patient ID is missing.');
      return;
    }

    if (!resolvedTranscriptionId || !resolvedEmrId) {
      toast.error(
        'transcription_id or emr_id is missing from extraction response.',
      );
      return;
    }

    try {
      setIsSavingEmr(true);

      const editedStructuredData = applyEditedValuesToStructuredData(
        structuredExtractionData,
        editablePreviewValues,
      );

      if (isCycleRequiredDocumentType) {
        setIsCheckingCycle(true);

        const cycleCheck = await checkPatientHasActiveCycle(Number(patientId));

        if (!cycleCheck.hasActiveCycle) {
          setPendingNoCycleFallback({
            documentType: selectedDocumentType,
            editedStructuredData,
          });
          setShowNoActiveCycleModal(true);
          return;
        }
      }

      const normalizedEmrData = normalizeEmrDataForDocumentType(
        selectedDocumentType,
        editedStructuredData,
      );

      await saveVoiceExtraction({
        patientId: Number(patientId),
        transcriptionId: resolvedTranscriptionId,
        emrId: resolvedEmrId,
        documentType: selectedDocumentType,
        emrData: normalizedEmrData,
      });

      toast.success('Data saved to EMR successfully.');
      onSaved?.();
    } catch (error) {
      toast.error(
        error instanceof Error
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          ? (error as any)?.errors?.[0]?.detail || error.message
          : 'Failed to save extracted data.',
      );
    } finally {
      setIsSavingEmr(false);
      setIsCheckingCycle(false);
    }
  };

  const renderWelcome = () => (
    <div className={styles.voiceFlowCenterState}>
      <div className={styles.welcomeOrbWrap}>
        <div className={styles.welcomeLoaderOverlay}>
          <AiLoader />
        </div>
      </div>

      <h3 className={styles.welcomeTitle}>Hello, Good Morning!</h3>
      <p className={styles.welcomeText}>Welcome to AI Voice Data Entry</p>

      <button
        type="button"
        className={styles.voiceMicPrimaryButton}
        onClick={() => void handleStartSession(false)}
        disabled={isStarting}
      >
        <Mic size={18} />
      </button>

      {extractionError && (
        <div className={styles.errorBox}>{extractionError}</div>
      )}
    </div>
  );

  const renderRecording = () => (
    <div className={styles.voiceFlowCenterState}>
      <div className={styles.welcomeOrbWrap}>
        <div className={styles.welcomeLoaderOverlay}>
          <AiLoader />
        </div>
      </div>

      <h3 className={styles.welcomeTitle}>Hello, Good Morning!</h3>
      <p className={styles.welcomeText}>
        {isRecording
          ? 'Live transcription in progress'
          : isPaused
            ? 'Transcription paused'
            : 'Preparing microphone'}
      </p>

      <div className={styles.voiceLiveTranscriptBox}>
        <div className={styles.voiceTranscriptBlock}>
          {transcriptText ||
            'Start speaking. Your live transcript will appear here...'}
        </div>
      </div>

      <div className={styles.welcomeText}>{formatDuration(elapsedSeconds)}</div>

      <div className={styles.voiceRecordingActions}>
        <button
          type="button"
          className={styles.voiceSecondaryActionButton}
          onClick={() => void handleCancelRecording()}
        >
          <X size={16} />
        </button>

        <button
          type="button"
          className={styles.voiceSecondaryActionButton}
          onClick={() => void handlePauseResume()}
          disabled={isStarting}
        >
          {isRecording ? <Pause size={16} /> : <Play size={16} />}
        </button>

        <button
          type="button"
          className={styles.voiceSecondaryActionButton}
          onClick={() => void handleNextToReview()}
          disabled={!transcriptText.trim()}
        >
          <Check size={16} />
        </button>
      </div>

      {extractionError && (
        <div className={styles.errorBox}>{extractionError}</div>
      )}
    </div>
  );
  const renderTranscript = () => (
    <div className={styles.voiceTranscriptStage}>
      <div className={styles.voiceTranscriptPanel}>
        <textarea
          value={transcriptText}
          onChange={(e) => {
            setTranscriptText(e.target.value);
            finalTranscriptRef.current = e.target.value;
          }}
          readOnly={!isTranscriptEditable}
          className={`${styles.voiceTranscriptDesignArea} ${
            isTranscriptEditable ? styles.voiceTranscriptDesignAreaEditable : ''
          }`}
          placeholder="Transcript will appear here..."
        />
      </div>

      <div className={styles.voiceTranscriptActionRow}>
        <button
          type="button"
          className={styles.voiceTranscriptSecondaryButton}
          onClick={() => setIsTranscriptEditable((prev) => !prev)}
        >
          <Pencil size={16} />
          {isTranscriptEditable ? 'Lock Transcript' : 'Edit Transcript'}
        </button>

        <button
          type="button"
          className={styles.voiceTranscriptPrimaryButton}
          onClick={handleProceedFromTranscript}
        >
          <Sparkles size={16} />
          Scan for AI Insights
        </button>
      </div>

      {extractionError ? (
        <div className={styles.errorBox}>{extractionError}</div>
      ) : null}
    </div>
  );

  const renderSelectPage = () => (
    <div className={styles.voiceDesignShell}>
      <div className={styles.voiceDesignOrbWrap}>
        <div className={styles.voiceDesignOrb}>
          <AiLoader />
        </div>
      </div>

      <p className={styles.voiceSelectText}>
        Please search &amp; select a page from below to run a AI scan
        <br />
        &amp; extract data
      </p>

      <div className={styles.voiceSelectSearchWrap}>
        <Search size={18} className={styles.searchIcon} />
        <input
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          placeholder="Search EMR page"
          className={styles.searchInput}
        />
      </div>

      <div className={styles.voiceSelectLabel}>Recommended Pages :</div>

      <div className={styles.voiceSelectChipWrap}>
        {filteredOptions.map((option) => (
          <button
            key={option.key}
            type="button"
            className={styles.voiceSelectChip}
            onClick={() => void runExtraction(option)}
            title={option.description}
          >
            {option.label}
          </button>
        ))}
      </div>

      <div className={styles.voiceBackRow}>
        <button
          type="button"
          className={styles.voiceBackButton}
          onClick={() => setStep('transcript')}
        >
          <ArrowLeft size={16} />
          Back
        </button>
      </div>

      {extractionError ? (
        <div className={styles.errorBox}>{extractionError}</div>
      ) : null}
    </div>
  );

  const renderLoading = () => (
    <div className={styles.voiceDesignShell}>
      <div className={styles.voiceDesignOrbWrap}>
        <div className={styles.voiceDesignOrb}>
          <AiLoader />
        </div>
      </div>

      <div className={styles.voiceLoadingText}>
        Scanning file for {selectedType?.label || 'selected page'}...
      </div>
    </div>
  );

  const renderSummary = () => (
    <div className={styles.voiceSummaryStage}>
      <div className={styles.voiceSummaryCard}>
        <div className={styles.voiceSummaryTitle}>{summaryHeading}</div>

        <ul className={styles.voiceSummaryList}>
          {aiSummaryLines.map((line, index) => (
            <li key={`${line}-${index}`} className={styles.voiceSummaryItem}>
              {line}
            </li>
          ))}
        </ul>
      </div>

      <div className={styles.voiceSummaryActionGrid}>
        <button
          type="button"
          className={styles.voiceSummaryGhostButton}
          onClick={() => setStep('select-page')}
        >
          <Sparkles size={14} />
          Scan Again
        </button>

        <button
          type="button"
          className={styles.voiceSummaryDarkButton}
          onClick={() => setStep('preview')}
        >
          Extract to Fields
        </button>
      </div>

      <button
        type="button"
        className={styles.voiceSummarySaveButton}
        onClick={() => void handleSaveSummaryToNotes()}
        disabled={isSavingSummary}
      >
        {isSavingSummary ? 'Saving Summary...' : 'Save Summary to Notes'}
      </button>
    </div>
  );

  const renderPreview = () => (
    <div className={styles.voicePreviewStage}>
      <div className={styles.voicePreviewCard}>
        {previewItems.length ? (
          previewItems.map((item) => {
            const indent = Math.min(item.depth, 4) * 12;

            if (item.kind === 'section') {
              return (
                <div
                  key={item.id}
                  className={styles.voicePreviewSectionHeading}
                  style={{ marginLeft: `${indent}px` }}
                >
                  {item.label}
                </div>
              );
            }

            return (
              <div
                key={item.id}
                className={styles.voicePreviewFieldCard}
                style={{ marginLeft: `${indent}px` }}
              >
                <label className={styles.voicePreviewFieldLabel}>
                  {item.label}
                </label>

                {item.isMultiline ? (
                  <textarea
                    className={styles.voicePreviewTextarea}
                    value={item.value}
                    rows={4}
                    onChange={(e) =>
                      setEditablePreviewValues((prev) => ({
                        ...prev,
                        [item.id]: e.target.value,
                      }))
                    }
                  />
                ) : (
                  <input
                    className={styles.voicePreviewInput}
                    value={item.value}
                    onChange={(e) =>
                      setEditablePreviewValues((prev) => ({
                        ...prev,
                        [item.id]: e.target.value,
                      }))
                    }
                  />
                )}
              </div>
            );
          })
        ) : (
          <div className={styles.structuredEmptyState}>
            No structured preview data was returned by the API.
          </div>
        )}
      </div>

      <div className={styles.voicePreviewFooter}>
        <button
          type="button"
          className={styles.cancelFieldsButton}
          onClick={() => setStep('summary')}
        >
          Cancel
        </button>

        <button
          type="button"
          className={styles.saveEmrButton}
          onClick={() => void handleSaveToEMR()}
          disabled={isSavingEmr || isCheckingCycle}
        >
          {isCheckingCycle
            ? 'Checking Cycle...'
            : isSavingEmr
              ? 'Saving...'
              : 'Save to EMR'}
        </button>
      </div>
    </div>
  );

  const renderError = () => (
    <div className={styles.voiceDesignShell}>
      <div className={styles.voiceDesignOrbWrap}>
        <div className={styles.voiceDesignOrb}>
          <AiLoader />
        </div>
      </div>

      <h3 className={styles.loadingTitle}>Extraction Failed</h3>
      <div className={styles.errorBox}>{extractionError}</div>

      <div className={styles.voiceSummaryActionGrid}>
        <button
          type="button"
          className={styles.voiceSummaryGhostButton}
          onClick={() =>
            setStep(transcriptText.trim() ? 'transcript' : 'welcome')
          }
        >
          Back
        </button>

        <button
          type="button"
          className={styles.voiceSummaryDarkButton}
          onClick={() => selectedType && void runExtraction(selectedType)}
        >
          Retry Extraction
        </button>
      </div>
    </div>
  );

  return (
    <div className={styles.voiceWorkbenchShell}>
      <div className={styles.extractCard}>
        <div className={styles.extractHeader}>
          <div className={styles.voiceWorkbenchHeaderRow}>
            <div className={styles.extractTitle}>{title}</div>

            <button
              type="button"
              className={styles.voiceWorkbenchNewButton}
              onClick={() => void resetWorkbench(false)}
            >
              <Pencil size={14} />
              New
            </button>
          </div>
        </div>

        <div className={`${styles.sidebarBody} ${styles.voiceSidebarBody}`}>
          {step === 'welcome' && renderWelcome()}
          {step === 'recording' && renderRecording()}
          {step === 'transcript' && renderTranscript()}
          {step === 'select-page' && renderSelectPage()}
          {step === 'loading' && renderLoading()}
          {step === 'summary' && renderSummary()}
          {step === 'preview' && renderPreview()}
          {step === 'error' && renderError()}
        </div>
      </div>

      {showNoActiveCycleModal && (
        <div className={styles.noCycleModalBackdrop}>
          <div className={styles.noCycleModalCard}>
            <div className={styles.noCycleModalHeader}>
              <h3 className={styles.noCycleModalTitle}>
                No Active Cycle Found
              </h3>

              <button
                type="button"
                className={styles.noCycleModalCloseButton}
                onClick={() => {
                  if (isSavingSummary) return;
                  setShowNoActiveCycleModal(false);
                  setPendingNoCycleFallback(null);
                }}
                disabled={isSavingSummary}
              >
                <X size={20} />
              </button>
            </div>

            <div className={styles.noCycleModalDivider} />

            <div className={styles.noCycleModalBody}>
              <div className={styles.noCycleModalHighlightBox}>
                <span className={styles.noCycleModalFloatingLabel}>
                  Cycle Validation
                </span>
                <div className={styles.noCycleModalHighlightValue}>
                  This patient does not have any active cycle.
                </div>
              </div>

              <p className={styles.noCycleModalMessage}>
                You can save the extracted result to AI Summary instead.
              </p>
            </div>

            <div className={styles.noCycleModalActions}>
              <button
                type="button"
                className={styles.noCycleModalSecondaryButton}
                onClick={() => {
                  if (isSavingSummary) return;
                  setShowNoActiveCycleModal(false);
                  setPendingNoCycleFallback(null);
                }}
                disabled={isSavingSummary}
              >
                Cancel
              </button>

              <button
                type="button"
                className={styles.noCycleModalPrimaryButton}
                onClick={() => void handleSaveFallbackSummaryFromModal()}
                disabled={isSavingSummary}
              >
                {isSavingSummary ? 'Saving...' : 'Save to Summary'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmbeddedVoiceToTextWorkbench;
